import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { bulkImportService } from '@/services/bulk-import.service';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';
import { BulkImportStepper } from './BulkImportStepper';
import { BulkImportUploadStage } from './BulkImportUploadStage';
import { BulkImportProcessingStage } from './BulkImportProcessingStage';
import { BulkImportReviewStage } from './BulkImportReviewStage';
import { BulkImportResultLinks } from './BulkImportResultLinks';
import {
  SESSION_STATUS_BADGE,
  SESSION_STATUS_LABELS,
  stageForStatus,
} from './bulk-import-ui.constants';

// VALIDATING is expected to resolve in seconds for realistic workbook sizes
// (Phase 2c/2d bound row counts at 1000). This ceiling only stops the
// automatic poll loop — it never affects the backend job itself.
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const POLLING_STATUSES = new Set(['VALIDATING', 'COMMITTING']);

export function BulkImportWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const pollStartRef = useRef<number | null>(null);
  const prevStatusRef = useRef<string | null>(null);

  const {
    data: session,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['bulk-import-session', id],
    queryFn: () => bulkImportService.get(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || !POLLING_STATUSES.has(status)) {
        pollStartRef.current = null;
        return false;
      }
      if (pollStartRef.current === null) pollStartRef.current = Date.now();
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) return false;
      return POLL_INTERVAL_MS;
    },
  });

  // Fires a result-aware notification exactly once, the moment polling
  // detects the session has left COMMITTING — never optimistically before
  // the backend actually confirms the outcome (§7's own explicit rule).
  useEffect(() => {
    const prev = prevStatusRef.current;
    const current = session?.status ?? null;
    if (prev === 'COMMITTING' && current && current !== 'COMMITTING') {
      if (current === 'COMPLETED') toast.success('Final Import completed');
      else if (current === 'PARTIALLY_COMPLETED')
        toast.info('Final Import partially completed — some rows remain unresolved');
      else if (current === 'FAILED') toast.error('Final Import failed');
    }
    prevStatusRef.current = current;
  }, [session?.status]);

  const invalidateSession = () =>
    void queryClient.invalidateQueries({ queryKey: ['bulk-import-session', id] });

  if (!id) return null;

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !session) {
    return (
      <div className="space-y-5">
        <Breadcrumb
          segments={[
            { label: 'Equipment Inward', to: ROUTES.EQUIPMENT_INTAKE },
            { label: 'Bulk Import' },
          ]}
        />
        <EmptyState
          icon={faCircleExclamation}
          title="Import session not found"
          description={getApiErrorMessage(error, 'This import session could not be loaded.')}
        />
      </div>
    );
  }

  const stage = stageForStatus(session.status);
  const timedOut =
    stage === 'processing' &&
    pollStartRef.current !== null &&
    Date.now() - pollStartRef.current > POLL_TIMEOUT_MS;

  return (
    <div className="space-y-5">
      <Breadcrumb
        segments={[
          { label: 'Equipment Inward', to: ROUTES.EQUIPMENT_INTAKE },
          { label: session.sourceFileName ?? `Session ${session.id.slice(0, 8)}` },
        ]}
      />

      <PageHeader
        title="Bulk Import Workspace"
        description={session.sourceFileName ?? 'Awaiting workbook upload'}
      />

      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${SESSION_STATUS_BADGE[session.status]}`}
        >
          {SESSION_STATUS_LABELS[session.status]}
        </span>
      </div>

      <BulkImportStepper current={stage} />

      {stage === 'upload' && <BulkImportUploadStage session={session} />}

      {stage === 'processing' && (
        <BulkImportProcessingStage
          timedOut={timedOut}
          onManualRefresh={() => void refetch()}
          isRefreshing={isRefetching}
          title={session.status === 'COMMITTING' ? 'Final Import In Progress' : undefined}
          description={
            session.status === 'COMMITTING'
              ? 'Eligible rows are being committed into Equipment, Receiving Lots, and Arrivals. This page updates automatically — do not resubmit.'
              : undefined
          }
        />
      )}

      {stage === 'review' && (
        <BulkImportReviewStage session={session} onSessionChanged={invalidateSession} />
      )}

      {stage === 'final' && (
        <div className="max-w-2xl space-y-4 rounded-lg border border-border bg-card p-6">
          {session.status === 'COMPLETED' ? (
            <>
              <p className="text-sm font-medium text-foreground">
                This import session is complete.
              </p>
              <p className="text-sm text-muted-foreground">
                {session.passExistingCount + session.passNewCount} row(s) were committed across this
                session's attempt(s). Rows that were never eligible (Fail/Conflict) or were
                explicitly abandoned via Finalize are not included.
              </p>
              <BulkImportResultLinks
                resultingReceivingLotId={session.resultingReceivingLotId}
                resultingArrivalId={session.resultingArrivalId}
              />
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">
                {session.status === 'FAILED'
                  ? 'This import failed and no data was written from this session.'
                  : session.status === 'CANCELLED'
                    ? 'This import session was cancelled.'
                    : session.status === 'EXPIRED'
                      ? 'This import session has expired.'
                      : 'This session has no further actions available.'}
              </p>
              {session.status === 'FAILED' && (
                <p className="text-sm text-muted-foreground">
                  No Equipment, Receiving Lot, or Arrival data was written for this session's
                  attempt. Review the workbook and start a new session to try again.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
